/**
 * FAT #19 Phase 3b — Beta nudge banners.
 *
 * Two states, mutually exclusive:
 *   • ≤7 days & active → dismissible gold nudge with "View plans" CTA.
 *   • !is_active       → persistent red-tinted "Beta has ended" banner
 *                        with "Upgrade Now" CTA, NOT dismissible.
 *
 * Both mount at the top of the Home / Dashboard route.
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

const dismissKey = () => `wc.betaNudge.dismissed.${new Date().toISOString().slice(0, 10)}`;

export function BetaCountdownBanner() {
  const { plan, isActive, daysRemaining } = useSubscription();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try { setDismissed(localStorage.getItem(dismissKey()) === "1"); } catch { /* noop */ }
  }, []);

  // Non-Beta plans use their own billing flow — skip these banners.
  if (plan !== "Beta") return null;

  // EXPIRED state (persistent, non-dismissible)
  if (isActive === false) {
    return (
      <div
        data-testid="beta-expired-banner"
        className="w-full bg-red-600 text-white text-sm font-medium px-4 py-3 flex items-center justify-center gap-4"
      >
        <AlertTriangle className="h-4 w-4" />
        <span>Your Beta has ended. Upgrade to keep growing.</span>
        <Link
          to="/upgrade?code=MEMBERSHIP_EXPIRED"
          data-testid="beta-expired-cta"
          className="rounded-full bg-white text-red-700 px-4 py-1 text-xs font-semibold hover:bg-red-50 transition"
        >
          Upgrade Now
        </Link>
      </div>
    );
  }

  // 7-DAY NUDGE state (dismissible per day)
  if (daysRemaining === null || daysRemaining === undefined) return null;
  if (daysRemaining > 7) return null;
  if (dismissed) return null;

  const copy =
    daysRemaining <= 0 ? "Your Beta ends today"
    : daysRemaining === 1 ? "Your Beta ends in less than a day"
    : `Your Beta ends in ${daysRemaining} days`;

  return (
    <div
      data-testid="beta-nudge-banner"
      className="w-full bg-[#FFC630] text-black text-sm font-medium px-4 py-2 flex items-center justify-center gap-4"
    >
      <span>{copy}</span>
      <Link
        to="/upgrade"
        data-testid="beta-nudge-cta"
        className="underline underline-offset-2 font-semibold hover:no-underline"
      >
        View plans →
      </Link>
      <button
        type="button"
        onClick={() => {
          try { localStorage.setItem(dismissKey(), "1"); } catch { /* noop */ }
          setDismissed(true);
        }}
        data-testid="beta-nudge-dismiss"
        aria-label="Dismiss"
        className="ml-1 text-xs opacity-70 hover:opacity-100"
      >
        ✕
      </button>
    </div>
  );
}

export default BetaCountdownBanner;
